import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Copy, Check } from 'lucide-react';
import { SmallText, BodyText } from '../brand/BrandTypography';
import { BrandCard } from '../brand/BrandCard';
import { getScriptsByRole } from '../../services/relationshipToolsService';

/**
 * Cycle Breaking Scripts
 * 
 * Pre-written scripts to help users know what to say when they
 * recognize they're in a negative cycle. Based on EFT principles.
 * Fetches scripts from the database dynamically.
 */

// Fallback data when database is unavailable
const FALLBACK_GENERAL_SCRIPTS = [
    {
        id: 'recognize-cycle',
        text: "I realize we are getting into our old negative cycle and that I am contributing to it.",
        context: "When you first notice the pattern"
    },
    {
        id: 'slow-down',
        text: "This feels familiar, and we both know where this goes. Can we slow down so I can be sure I'm meeting your needs?",
        context: "To pause and redirect"
    },
    {
        id: 'want-closeness',
        text: "I would like to be safe and close to you rather than distant, disconnected, alone, and afraid of what will happen.",
        context: "To express what you really want"
    }
];

// Transform database format to component format
const transformScript = (dbScript) => ({
    id: dbScript.script_id,
    text: dbScript.text,
    context: dbScript.context,
    role: dbScript.role
});

// Custom hook for fetching scripts
export const useScriptsData = (role = 'general') => {
    const [scripts, setScripts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const data = await getScriptsByRole(role);
                setScripts(data.map(transformScript));
                setLoading(false);
            } catch (err) {
                console.error('Error fetching scripts:', err);
                setScripts(FALLBACK_GENERAL_SCRIPTS);
                setError(err.message);
                setLoading(false);
            }
        };
        fetchData();
    }, [role]);

    return { scripts, loading, error };
};

// Legacy exports for backwards compatibility
export const GENERAL_SCRIPTS = FALLBACK_GENERAL_SCRIPTS;
export const PURSUER_SCRIPTS = [];
export const WITHDRAWER_SCRIPTS = [];

export const CycleBreakingScripts = ({ 
    role = 'both', 
    onCopy, 
    copiedScript,
    className 
}) => {
    const [scripts, setScripts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchScripts = async () => {
            try {
                // Map 'both' to 'general' for the API
                const apiRole = role === 'both' ? 'general' : role;
                const data = await getScriptsByRole(apiRole);
                setScripts(data.map(transformScript));
            } catch (err) {
                console.error('Error fetching scripts:', err);
                setScripts(FALLBACK_GENERAL_SCRIPTS);
            } finally {
                setLoading(false);
            }
        };
        fetchScripts();
    }, [role]);

    if (loading) {
        return (
            <div className={cn('flex items-center justify-center py-8', className)}>
                <div className="w-8 h-8 rounded-full border-2 border-[#7D8E95] border-t-transparent animate-spin" />
            </div>
        );
    }

    return (
        <div className={cn('space-y-4', className)}>
            {/* Role-specific section header */}
            {(role === 'pursuer' || role === 'withdrawer') && (
                <div className="text-center mb-6">
                    <SmallText className="text-[var(--orenda-text-muted)]">
                        Scripts tailored for {role === 'pursuer' ? 'the one who reaches' : 'the one who steps back'}
                    </SmallText>
                </div>
            )}

            {scripts.map((script, index) => (
                <BrandCard 
                    key={script.id} 
                    variant="elevated" 
                    padding="lg"
                    className="group"
                >
                    <div className="flex items-start gap-3 mb-3">
                        <div className="w-6 h-6 rounded-full bg-[var(--orenda-soft-teal)]/20 flex items-center justify-center flex-shrink-0">
                            <SmallText className="text-[#1D6B6B] font-medium text-xs">
                                {index + 1}
                            </SmallText>
                        </div>
                        <SmallText className="text-[var(--orenda-text-muted)]">
                            {script.context}
                        </SmallText>
                    </div>
                    
                    <BodyText className="text-[var(--orenda-text-primary)] leading-relaxed mb-4 italic">
                        "{script.text}"
                    </BodyText>
                    
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => onCopy(script.text, script.id)}
                            data-testid={`copy-script-${script.id}`}
                            className={cn(
                                'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all',
                                copiedScript === script.id
                                    ? 'bg-[var(--orenda-soft-teal)]/20 text-[#1D6B6B]'
                                    : 'bg-[var(--orenda-border)]/50 text-[var(--orenda-text-muted)] hover:bg-[var(--orenda-border)]'
                            )}
                        >
                            {copiedScript === script.id ? (
                                <>
                                    <Check className="w-3 h-3" />
                                    Copied
                                </>
                            ) : (
                                <>
                                    <Copy className="w-3 h-3" />
                                    Copy
                                </>
                            )}
                        </button>
                    </div>
                </BrandCard>
            ))}

            {/* Additional guidance */}
            <div className="p-4 rounded-xl bg-[#7D8E95]/10 border border-[#7D8E95]/20 mt-6">
                <SmallText className="text-[var(--orenda-text-secondary)] text-center leading-relaxed">
                    <strong>Remember:</strong> You don't have to use these exact words. 
                    What matters is shifting from protecting yourself to protecting your relationship.
                </SmallText>
            </div>
        </div>
    );
};

export default CycleBreakingScripts;
